import {
  isAdvertisingAllowed,
  isAdvertisingExplicitlyDenied,
} from "../Helpers/cookieConsent";

const scriptExists = (src: string) =>
  typeof window !== "undefined" && document.querySelector(`[src="${src}"]`);

// Initialize BIFF command queue early
const initBiffCommandQueue = () => {
  if (typeof window === "undefined") return;

  window.brg = window.brg || {};
  window.brg.ui = window.brg.ui || {};
  window.brg.ui.biff = window.brg.ui.biff || {
    withAuctions: () => ({ load: () => Promise.resolve() }),
    load: () => Promise.resolve(),
    cmd: [],
  };
  window.brg.ui.biff.cmd = window.brg.ui.biff.cmd || [];
};

// Runs the BIFF config load through the command queue and resolves/rejects
// based on biff.load(). Extracted so it can be re-invoked on retry: the config
// fetch (cdn.biff.travel) intermittently fails, and biff.load() is the call
// that re-fetches it. Returning early when the script already exists (see
// initBiff) would skip this entirely, so retries MUST go through here again.
const loadBiffViaQueue = (isLoggedIn: boolean): Promise<void> => {
  return new Promise((resolve, reject) => {
    window?.brg?.ui?.biff?.cmd?.push((biff) => {
      // TODO ask biff team to accept ICARUS as platformName
      // WORKAROUND: Temporarily change platformName to SUNRISE for BIFF
      // while preserving the original ICARUS value for tracking.
      let originalPlatformName: string | undefined;
      const visitEvent = window.platformData?.find(
        (item: any) => item.event === "visit"
      );

      if (visitEvent?.page?.platformName === "ICARUS") {
        originalPlatformName = visitEvent.page.platformName;
        visitEvent.page.platformName = "SUNRISE";
      }

      const restorePlatformName = () => {
        if (originalPlatformName && visitEvent?.page) {
          visitEvent.page.platformName = originalPlatformName;
        }
      };

      // Enable auctions and load BIFF (as per docs)
      biff.withAuctions();
      biff
        .load({ isLoggedIn })
        .then(() => {
          restorePlatformName();
          resolve();
        })
        .catch((error: unknown) => {
          restorePlatformName();
          reject(error instanceof Error ? error : new Error(String(error)));
        });
    });
  });
};

// Initialize BIFF. Safe to call multiple times: when the scripts are already
// present (e.g. on a retry) it re-runs only the config load, not the script
// injection.
export const initBiff = (
  isProduction: boolean,
  isLoggedIn: boolean
): Promise<void> => {
  return new Promise((resolve, reject) => {
    if (typeof window === "undefined") {
      reject(new Error("Window not available"));
      return;
    }

    const biffSrc = "https://cdn.biff.travel/scripts/8/biff.min.js";

    // Initialize command queue first
    initBiffCommandQueue();

    // Set API endpoint based on environment (v4 as per official BIFF docs)
    const apiEndpoint = isProduction
      ? "https://cdn.biff.travel/api/v4/"
      : "https://qa-cdn.biff.travel/api/v4/";

    // Set envConfig for BIFF (as per official docs)
    window.envConfig = {
      ...window.envConfig,
      biffUrl: apiEndpoint,
    };

    // Scripts already present (e.g. on a retry): just re-run the config load.
    if (scriptExists(biffSrc)) {
      loadBiffViaQueue(isLoggedIn).then(resolve).catch(reject);
      return;
    }

    // Load biff script
    const biffScript = document.createElement("script");
    biffScript.src = biffSrc;
    biffScript.async = true;
    document.head.appendChild(biffScript);

    biffScript.onload = () => {
      const runLoad = () =>
        loadBiffViaQueue(isLoggedIn).then(resolve).catch(reject);

      // Load GPT script first
      const gptSrc = "https://securepubads.g.doubleclick.net/tag/js/gpt.js";
      if (!scriptExists(gptSrc)) {
        const gptScript = document.createElement("script");
        gptScript.src = gptSrc;
        gptScript.async = true;
        document.head.appendChild(gptScript);

        gptScript.onload = runLoad;

        gptScript.onerror = () => {
          reject(new Error("Failed to load GPT script"));
        };
      } else {
        // GPT script already loaded
        runLoad();
      }
    };

    biffScript.onerror = () => {
      reject(new Error("Failed to load biff script"));
    };
  });
};

// Re-render any defined GPT slot whose element is currently empty. Used as a
// landing-page safety net: the per-AdSlot refresh only fires on (re)mount, so on
// the very first page the slot isn't defined yet when AdSlot mounts and the
// refresh no-ops — leaving the first page dependent on BIFF's initial display(),
// which can race ahead of the ad div being ready. Called once after init: filled
// slots are skipped (no wasted request), only genuinely-empty ones re-render.
export const refreshEmptyAdSlots = () => {
  if (typeof window === "undefined") return;
  const pubads = window.googletag?.pubads?.();
  const emptySlots = (pubads?.getSlots?.() ?? []).filter(
    (slot: { getSlotElementId: () => string }) => {
      const el = document.getElementById(slot.getSlotElementId());
      return !!el && el.children.length === 0;
    }
  );
  if (emptySlots.length) pubads?.refresh?.(emptySlots);
};

// Check for cookie consent and visit event before enabling ads
// Maximum time to wait for consent (in seconds) to prevent infinite polling
const MAX_CONSENT_WAIT_SECONDS = 60;

// Window events the consent layer dispatches once cookie consent is processed.
// BIFF itself gates slot definition on the "LMN_COOKIES_GO" event; Tealium/CCM
// dispatch the equivalent "cookieGO". We listen so we can react immediately
// instead of waiting for the next poll tick.
const CONSENT_READY_EVENTS = ["LMN_COOKIES_GO", "cookieGO"];

// The lastminute-group "consent processed" signal: the global flag BIFF reads,
// or the Tealium/CCM cookieGO event recorded in platformData. Used as an extra
// readiness path so a missing/slow legacy oil_GTM_cookie no longer silently
// blocks ads — explicit advertising denial (checked first) always takes
// precedence, so this never serves ads to users who declined.
const hasGroupConsentSignal = (): boolean =>
  window.LMN_COOKIES_GO === true ||
  (window.platformData?.some(
    (event: any) =>
      event?.event === "Tealium_cookieGO" || event?.event === "CCM_cookieGO"
  ) ??
    false);

// Diagnostics emitted once per consent-gate resolution. Temporary instrumentation
// to decide whether the poll fallback is still needed or the gate can go pure
// event-driven, and whether the legacy oil_GTM_cookie is ever the sole consent
// source (i.e. whether we can drop it later). See useBiffInitialization logging.
export interface ConsentDiagnostics {
  // Final result of the gate.
  outcome: "init" | "denied" | "timeout";
  // Which trigger produced the resolving evaluation.
  resolvedBy: "immediate" | "event" | "poll";
  // How many 1s poll ticks elapsed before resolving.
  pollTicks: number;
  // Wall-clock time from start to resolution.
  waitMs: number;
  // Which consent signal satisfied the gate (only meaningful when outcome=init).
  consentSource: "oil" | "group" | "both" | "none";
  // What the gate was still waiting on at the very first check.
  blockedOnAtStart: "none" | "consent" | "visit" | "both";
}

export const waitForCookieConsent = (): Promise<{
  shouldInitializeBiff: boolean;
  diagnostics: ConsentDiagnostics;
}> => {
  return new Promise((resolve) => {
    const startedAt = Date.now();

    if (typeof window === "undefined") {
      resolve({
        shouldInitializeBiff: false,
        diagnostics: {
          outcome: "timeout",
          resolvedBy: "immediate",
          pollTicks: 0,
          waitMs: 0,
          consentSource: "none",
          blockedOnAtStart: "none",
        },
      });
      return;
    }

    let settled = false;
    let retryCount = 0;
    let pollTimer: ReturnType<typeof setTimeout> | undefined;
    let firstCheck = true;
    let blockedOnAtStart: ConsentDiagnostics["blockedOnAtStart"] = "none";

    const cleanup = () => {
      if (pollTimer) clearTimeout(pollTimer);
      CONSENT_READY_EVENTS.forEach((name) =>
        window.removeEventListener(name, onConsentEvent)
      );
    };

    const settle = (
      shouldInitializeBiff: boolean,
      diagnostics: ConsentDiagnostics
    ) => {
      if (settled) return;
      settled = true;
      cleanup();
      resolve({ shouldInitializeBiff, diagnostics });
    };

    function evaluate(resolvedBy: ConsentDiagnostics["resolvedBy"]) {
      if (settled) return;
      // Keep at most one pending poll, whether triggered by the timer or an event.
      if (pollTimer) {
        clearTimeout(pollTimer);
        pollTimer = undefined;
      }

      const denied = isAdvertisingExplicitlyDenied();
      const oilOk = isAdvertisingAllowed();
      const groupOk = hasGroupConsentSignal();
      const hasConsent = oilOk || groupOk;
      const hasVisitEvent =
        window.platformData?.some((event: any) => event.event === "visit") ??
        false;

      // Record what we were blocked on at the very first check (for telemetry).
      if (firstCheck) {
        firstCheck = false;
        if (!denied) {
          if (!hasConsent && !hasVisitEvent) blockedOnAtStart = "both";
          else if (!hasConsent) blockedOnAtStart = "consent";
          else if (!hasVisitEvent) blockedOnAtStart = "visit";
        }
      }

      // Users who explicitly declined advertising are never served ads,
      // regardless of the group consent signal.
      if (denied) {
        settle(false, {
          outcome: "denied",
          resolvedBy,
          pollTicks: retryCount,
          waitMs: Date.now() - startedAt,
          consentSource: "none",
          blockedOnAtStart,
        });
        return;
      }

      if (hasConsent && hasVisitEvent) {
        // Extract visit event data and add to platformData for BIFF
        const visitEvent = window.platformData?.find(
          (event: any) => event.event === "visit"
        );

        if (visitEvent) {
          // Add visit event properties directly to platformData object for BIFF
          Object.assign(window.platformData, {
            transactionAffiliation: visitEvent.transactionAffiliation,
            page: visitEvent.page,
            search: visitEvent.search,
            user: visitEvent.user,
            product: visitEvent.product,
          });
        }

        settle(true, {
          outcome: "init",
          resolvedBy,
          pollTicks: retryCount,
          waitMs: Date.now() - startedAt,
          consentSource: oilOk && groupOk ? "both" : oilOk ? "oil" : "group",
          blockedOnAtStart,
        });
        return;
      }

      // Not ready yet — keep polling as a fallback (covers the oil-cookie path
      // and the visit event arriving), up to the max wait.
      if (retryCount >= MAX_CONSENT_WAIT_SECONDS) {
        settle(false, {
          outcome: "timeout",
          resolvedBy,
          pollTicks: retryCount,
          waitMs: Date.now() - startedAt,
          consentSource: "none",
          blockedOnAtStart,
        });
        return;
      }
      retryCount++;
      pollTimer = setTimeout(() => evaluate("poll"), 1000);
    }

    const onConsentEvent = () => evaluate("event");

    // React immediately when the consent layer signals, with polling as fallback.
    CONSENT_READY_EVENTS.forEach((name) =>
      window.addEventListener(name, onConsentEvent, { passive: true })
    );

    evaluate("immediate");
  });
};
